import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfirmCandidateData } from '~/components/ConfirmCandidate/hooks/useConfirmCandidateData';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { IJob, IMultipleCounters } from '~/models';

interface IUseConfirmCandidatesDataArgs {
  shiftId?: string;
  /**
   * The route's `:candidateId`, when it names one.
   *
   * Honoured here but deliberately not in the legacy screen's sense: that screen
   * rewrote the param to the first applicant as soon as the list settled, which
   * is why this page ignored it at first. The redesign never rewrites it, so the
   * only things that can set it are an office following a deep link from an
   * email, and the decline mutation advancing to the next candidate — both of
   * which are a choice worth honouring.
   */
  routeCandidateId?: string;
}

/**
 * The page's reads, plus which of its two views they are being read for.
 *
 * Data and selection are one hook because they are genuinely coupled: the
 * selected candidate scopes the fetch. `useConfirmCandidateData` takes a
 * `candidateId` to load that candidate's overtime and credentials, so the
 * selection has to exist *before* the query runs — which is why the sole
 * applicant is adopted as the selection in an effect rather than derived below
 * the call. Deriving it there leaves the two disagreeing, and the fetch is what
 * loses: see `openCandidateId`.
 *
 * The whole one-vs-many question is answered here: with several applicants the
 * list is the landing page and opening a row selects a candidate; with exactly
 * one there is nothing to choose, so that candidate *is* the selection.
 */
export const useConfirmCandidatesData = ({
  shiftId,
  routeCandidateId,
}: IUseConfirmCandidatesDataArgs) => {
  /**
   * Which candidate's own page is open, or `null` for the list.
   *
   * Page state rather than a route param: the confirm route already carries a
   * `:candidateId`, but the legacy screen rewrites it to the first applicant as
   * soon as the list settles, so it records no choice the office made.
   */
  const [openCandidateId, setOpenCandidateId] = useState<string | null>(routeCandidateId ?? null);


  const { job, allCandidates, loadingCandidates, isSuccessFetchJobCandidates, authorizations } =
    useConfirmCandidateData({ jobId: shiftId, candidateId: openCandidateId ?? undefined });

  /**
   * `useConfirmCandidateData` is JavaScript, so its React Query payloads arrive
   * untyped. Naming the shape once here keeps the assertion at the boundary
   * instead of at every use.
   *
   * Memoised because it feeds the impression effect: the `?? []` would otherwise
   * mint a new array on every render and re-fire `candidate_viewed`.
   */
  const counters = useMemo(() => (allCandidates ?? []) as IMultipleCounters[], [allCandidates]);

  const isSingleCandidate = counters.length === 1;

  const soleCandidateId = isSingleCandidate ? (counters[0]?.candidate?.id ?? null) : null;

  /**
   * The one place `openCandidateId` is decided, so the two things that can
   * decide it cannot fight over it.
   *
   * It scopes the credentials and overtime queries, so a candidate on screen
   * without being *selected* renders from data fetched for somebody else, or for
   * nobody: an empty Credentials tab, and a receipt carrying the shift's fees
   * rather than their overtime-merged ones.
   *
   * In precedence:
   *
   * 1. **The route, when it names somebody.** `useDeclineCandidateMutation`
   *    advances to the next applicant by replacing the URL, and an email deep
   *    links straight to a candidate. Ignoring it drops the office back on the
   *    list after every decline.
   * 2. **The sole applicant.** They never go through the list, so nothing ever
   *    calls `openCandidate`; and declining down to one survivor would otherwise
   *    leave the declined candidate selected, showing their licences under the
   *    survivor's name.
   * 3. **Nobody.** The page also renders on `/dashboard/job/:jobId`, so a route
   *    that names no candidate is an instruction to show the list — not an
   *    absence of one. Without this the last pick stays on screen while the URL
   *    says otherwise.
   */
  useEffect(() => {
    const next = routeCandidateId ?? soleCandidateId ?? null;

    setOpenCandidateId((current) => (current === next ? current : next));
  }, [routeCandidateId, soleCandidateId]);

  const activeCounter = isSingleCandidate
    ? counters[0]
    : (counters.find((counter) => counter?.candidate?.id === openCandidateId) ?? null);

  const clearSelection = useCallback(() => setOpenCandidateId(null), []);

  return {
    job: job as Partial<IJob> | undefined,
    authorizations: authorizations as IAuthorization[] | null,
    isLoading: Boolean(loadingCandidates),
    hasLoadedCandidates: Boolean(isSuccessFetchJobCandidates),
    counters,
    isSingleCandidate,
    activeCounter,
    openCandidate: setOpenCandidateId,
    clearSelection,
  };
};
